// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useState } from 'react';

import { isoTimestamp, type User, type WorkOrder, type WorkOrderStateName } from '@evolvemep/api-ts';
import { Card, Text } from '@mantine/core';

import type { ModalRegistryManagedProps } from 'components/Mantine/Modals/createModalRegistry';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { isNil } from 'helpers/isNotNil';
import { validateUserNameOrEmail } from 'helpers/userHelpers';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { useUser } from 'hooks/user/useUser';

const verbiageMap: Partial<
  Record<
    WorkOrderStateName,
    {
      progressive: string;
      past: string;
      action: string;
    }
  >
> = {
  Rejected: {
    progressive: 'rejection',
    past: 'rejected',
    action: 'Accept',
  },
  Canceled: {
    progressive: 'cancellation',
    past: 'cancelled',
    action: 'Close',
  },
} as const;

/** The rejection/cancellation verbiage for a work order state, or `undefined` if the state has no reason to show. */
export const getWorkOrderStatusVerbiage = (stateName: WorkOrderStateName) => verbiageMap[stateName];

export const getWorkOrderRejectedStorageKey = (user: User, workOrder: WorkOrder) =>
  `${user.userId}-${workOrder.identifier}-${workOrder.workOrderStateName}-accepted` as const;

type Props = ModalRegistryManagedProps & {
  workOrder: WorkOrder;
  /** Disables accepting for a moment, so a prompt that opened on its own isn't dismissed by a stray click. */
  delayAccept?: boolean;
};

/**
 * Shows why a work order was rejected/cancelled. Accepting records it, so `useWorkOrderStateModals` doesn't
 * auto-open it again for 23h. Renders nothing outside the relevant states.
 */
export const WorkOrderRejectedModal = ({ workOrder, delayAccept = false, ...props }: Props) => {
  const { user } = useUser();
  const [canAccept, setCanAccept] = useState(!delayAccept);
  const [, setLastStatusUpdateAccepted] = useLocalStorage(getWorkOrderRejectedStorageKey(user, workOrder), null);

  useEffect(() => {
    if (!delayAccept) return undefined;
    const timeout = setTimeout(() => setCanAccept(true), 1500);
    return () => clearTimeout(timeout);
  }, [delayAccept]);

  const verbiage = getWorkOrderStatusVerbiage(workOrder.workOrderStateName);

  if (isNil(verbiage)) return null;

  return (
    <WrappedModal
      title={`Work order ${verbiage.progressive}`}
      onConfirm={() => setLastStatusUpdateAccepted(isoTimestamp(new Date()))}
      disabled={!canAccept}
      withCloseButton={false}
      withCancelButton={false}
      {...props}
    >
      <Text>
        <b>{workOrder.identifier}</b> was {verbiage.past} by{' '}
        <b>{validateUserNameOrEmail(workOrder.workOrderStateCreatedBy)}</b> for the following reason:
      </Text>
      <Card fz="sm" bg="gray.1" p="xs">
        <Text>{workOrder.workOrderStateComments}</Text>
      </Card>
    </WrappedModal>
  );
};
